/** Format a Date as YYYY-MM-DD using local time. */
export function fmtDate(d: Date): string {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// ============== Tertial helpers ==============
// IKEA tertials split the fiscal year (Sep 1 – Aug 31) into three 4-month periods:
//   T1: Sep 1 – Dec 31
//   T2: Jan 1 – Apr 30
//   T3: May 1 – Aug 31
// Period value format: "tertial:<T>:<FY>" e.g. "tertial:1:26" = T1 FY26

function tertialDates(t: number, fullFy: number): { start: Date; endInclusive: Date } {
    // FY26 = Sep 2025 – Aug 2026
    switch (t) {
        case 1: return { start: new Date(fullFy - 1, 8, 1), endInclusive: new Date(fullFy - 1, 11, 31) };
        case 2: return { start: new Date(fullFy, 0, 1), endInclusive: new Date(fullFy, 3, 30) };
        case 3: return { start: new Date(fullFy, 4, 1), endInclusive: new Date(fullFy, 7, 31) };
        default: throw new Error(`Invalid tertial: ${t}`);
    }
}

function parseTertialValue(period: string): { t: number; fy: number } | undefined {
    const m = period.match(/^tertial:(\d):(\d+)$/);
    if (!m) return undefined;
    return { t: parseInt(m[1]), fy: parseInt(m[2]) };
}

/** Parse a tertial period string into API date params (start inclusive, end exclusive). */
export function parseTertialDates(period: string): { start_date: string; end_date: string } | undefined {
    const parsed = parseTertialValue(period);
    if (!parsed) return undefined;
    const fullFy = parsed.fy < 100 ? 2000 + parsed.fy : parsed.fy;
    const { start, endInclusive } = tertialDates(parsed.t, fullFy);
    const endExclusive = new Date(endInclusive);
    endExclusive.setDate(endExclusive.getDate() + 1);
    return { start_date: fmtDate(start), end_date: fmtDate(endExclusive) };
}

/** Build closed tertial period options for dropdowns (up to 3 most recent). */
export function buildClosedTertials(): Array<{ value: string; label: string; tooltip: string }> {
    const now = new Date();
    const DATA_START = new Date(2025, 8, 1).getTime(); // Sep 1, 2025

    // Determine current tertial
    const month = now.getMonth(); // 0-indexed
    let currentT: number, currentFy: number;
    if (month >= 8) { currentT = 1; currentFy = now.getFullYear() + 1; }
    else if (month >= 4) { currentT = 3; currentFy = now.getFullYear(); }
    else { currentT = 2; currentFy = now.getFullYear(); }

    // Walk backwards from previous tertial, collect up to 3 closed ones
    const result: Array<{ value: string; label: string; tooltip: string }> = [];
    let t = currentT;
    let fy = currentFy;
    // Step to previous tertial
    t--;
    if (t === 0) { t = 3; fy--; }

    for (let i = 0; i < 3; i++) {
        const dates = tertialDates(t, fy);
        if (dates.start.getTime() < DATA_START) break;

        const fyShort = fy % 100;
        result.unshift({
            value: `tertial:${t}:${fyShort}`,
            label: `T${t} FY${fyShort}`,
            tooltip: `Tertial ${t}, FY${fyShort} (${fmtDate(dates.start)} – ${fmtDate(dates.endInclusive)})`,
        });

        t--;
        if (t === 0) { t = 3; fy--; }
    }

    return result;
}

/**
 * Convert an exclusive end date (as used by the backend) to an inclusive
 * end date for display. Users think of date ranges as inclusive on both ends,
 * e.g. "March 1 – March 16" means data through March 16.
 */
export function inclusiveEndDate(exclusiveEnd: string): string {
    const [y, m, d] = exclusiveEnd.split("-").map(Number);
    return fmtDate(new Date(y, m - 1, d - 1));
}

/** Add one day to make an inclusive user-picked end date exclusive for the backend. */
export function exclusiveEndDate(inclusiveEnd: string): string {
    const [y, m, d] = inclusiveEnd.split("-").map(Number);
    return fmtDate(new Date(y, m - 1, d + 1));
}

/**
 * Compute the start date (ISO string) for a period value.
 * Returns undefined for periods that can't be resolved (custom, unknown).
 * Used to check against data_available_from.
 */
export function periodStartDate(period: string): string | undefined {
    // Week-based periods: last N complete Ingka weeks (Sun-Sat)
    const wm = period.match(/^(\d+)w$/);
    if (wm) {
        const weeks = parseInt(wm[1]);
        const today = new Date();
        const curSun = new Date(today.getFullYear(), today.getMonth(), today.getDate() - today.getDay());
        const start = new Date(curSun);
        start.setDate(start.getDate() - weeks * 7);
        return fmtDate(start);
    }
    // Tertial period
    const tp = parseTertialValue(period);
    if (tp) {
        const fullFy = tp.fy < 100 ? 2000 + tp.fy : tp.fy;
        const { start } = tertialDates(tp.t, fullFy);
        return fmtDate(start);
    }
    // FY: Sep 1 of current FY
    if (period === "fy") {
        const today = new Date();
        const fyStart = today.getMonth() >= 8
            ? new Date(today.getFullYear(), 8, 1)
            : new Date(today.getFullYear() - 1, 8, 1);
        return fmtDate(fyStart);
    }
    return undefined;
}

/**
 * Compute the inclusive date range label for a period string.
 * Week-based periods use Ingka weeks (Sun–Sat).
 * Returns "YYYY-MM-DD — YYYY-MM-DD" or undefined for unknown periods.
 */
export function periodDateRange(period: string): string | undefined {
    // Custom period: dates stored in the string (already inclusive)
    if (period.startsWith("custom:")) {
        const [, start, end] = period.split(":");
        return start && end ? `${start} — ${end}` : undefined;
    }
    // Week-based periods: last N complete Ingka weeks (Sun-Sat)
    const wm = period.match(/^(\d+)w$/);
    if (wm) {
        const weeks = parseInt(wm[1]);
        const today = new Date();
        // Sunday of the current (possibly incomplete) Ingka week
        const curSun = new Date(today.getFullYear(), today.getMonth(), today.getDate() - today.getDay());
        const start = new Date(curSun);
        start.setDate(start.getDate() - weeks * 7);
        const end = new Date(curSun);
        end.setDate(end.getDate() - 1); // last Saturday (inclusive)
        return `${fmtDate(start)} — ${fmtDate(end)}`;
    }
    // Tertial period: "tertial:T:FY"
    const tp = parseTertialValue(period);
    if (tp) {
        const fullFy = tp.fy < 100 ? 2000 + tp.fy : tp.fy;
        const dates = tertialDates(tp.t, fullFy);
        return `${fmtDate(dates.start)} — ${fmtDate(dates.endInclusive)}`;
    }
    // FY: Sep 1 through yesterday
    if (period === "fy") {
        const today = new Date();
        const fyStart = today.getMonth() >= 8
            ? new Date(today.getFullYear(), 8, 1)
            : new Date(today.getFullYear() - 1, 8, 1);
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        return `${fmtDate(fyStart)} — ${fmtDate(yesterday)}`;
    }
    return undefined;
}

/** Periods at/above this span (weeks) trigger the heavy-load treatment. */
const HEAVY_WEEKS = 16;

/**
 * True for long parquet scans (16w+, wide custom ranges, and FY once enough
 * of the year has elapsed).
 *
 * FY (Sep 1–today) is *not* inherently heavy: in mid-September it is only
 * ~2 weeks of data, while in mid-August it is ~50 weeks. Its weight therefore
 * depends on how far into the year we are, so we measure the actual elapsed
 * span rather than hardcoding it as heavy.
 */
export function isHeavyPeriod(period: string): boolean {
    const weeks = period.match(/^(\d+)w$/);
    if (weeks) return parseInt(weeks[1], 10) >= HEAVY_WEEKS;
    if (period.startsWith("custom:")) {
        const [, start, end] = period.split(":");
        if (start && end) {
            const startMs = Date.parse(start);
            const endMs = Date.parse(end);
            if (!Number.isNaN(startMs) && !Number.isNaN(endMs)) {
                const days = (endMs - startMs) / (1000 * 60 * 60 * 24);
                return days >= HEAVY_WEEKS * 7;
            }
        }
        return false;
    }
    if (period === "fy") {
        const startIso = periodStartDate(period);
        if (!startIso) return false;
        const startMs = Date.parse(startIso);
        if (Number.isNaN(startMs)) return false;
        const days = (Date.now() - startMs) / (1000 * 60 * 60 * 24);
        return days >= HEAVY_WEEKS * 7;
    }
    // Tertials are fixed ~4-month (~17 week) spans — always heavy, matching the
    // backend's date-range heaviness check.
    const tp = parseTertialValue(period);
    if (tp) {
        const fullFy = tp.fy < 100 ? 2000 + tp.fy : tp.fy;
        const { start, endInclusive } = tertialDates(tp.t, fullFy);
        const days = (endInclusive.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);
        return days >= HEAVY_WEEKS * 7;
    }
    return false;
}
